import React, { useRef, useEffect, useState, useMemo, useCallback } from "react";
// core components
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
// import Button from "components/CustomButtons/Button.js";
import Button from "@material-ui/core/Button";
import Table from "components/ReactTable/ReactTableV2";
import TextFieldRender from "components/TextFieldRender/TextFieldRender";
import TextFieldTableCell from "components/TextFieldTableCell/TextFieldTableCell";
import { useConfirmationDialog } from "hooks/useConfirmationDialog";
import useSendQuoteEmail from "hooks/useSendQuoteEmail";
import QuoteService from "services/QuoteService";
import { useSnackbar } from "hooks/useSnackbar";
import CircularProgress from "@material-ui/core/CircularProgress";

// libs
import InputAdornment from "@material-ui/core/InputAdornment";
import { Search as SearchIcon } from "components/MaterialSymbol/MaterialIcons";
import { Add as AddIcon } from "components/MaterialSymbol/MaterialIcons";
import { Remove as RemoveIcon } from "components/MaterialSymbol/MaterialIcons";
import { InfoOutlined as InfoOutlinedIcon } from "components/MaterialSymbol/MaterialIcons";
import IconButton from "@material-ui/core/IconButton";
import { Box, Typography, Divider, Tooltip, Input } from "@material-ui/core";
import { useFormik } from "formik";
import * as yup from "yup";
import { NumericFormat } from "react-number-format";
import PartSelectionDialog from "views/HomePageSpecials/PartSelectionDialog";
import SelectDealerGrouped from "components/SelectDealerGrouped/SelectDealerGrouped";

// Esquema de validación Yup
const validationSchema = yup.object({
    quoteRef: yup.string().required("Quote Ref is required"),
    labelManager: yup.string(),
    description: yup.string(),
    customerName: yup.string().required("Customer Name is required"),
    customerEmail: yup.string().email("Invalid email").required("Customer Email is required"),
    rows: yup
        .array()
        .of(
            yup.object().shape({
                partNumber: yup.string(),
                itemName: yup.string(),
                price: yup.number().typeError("Must be a number").nullable(),
                install: yup.number().typeError("Must be a number").nullable(),
                qty: yup.number().integer().typeError("Must be an integer").nullable(),
                // Guardamos `product` para referencia, pero no lo mostramos directamente
                product: yup.object().nullable(),
            }),
        )
        .test(
            "at-least-one-filled",
            "Please add at least one product to your quote before submitting",
            (rows) =>
                rows.some(
                    (row) => row.partNumber || row.itemName || row.price || row.install || row.qty,
                ),
        ),
});

export default function EditQuoteV2({ dlid, form, quote, onSuccess = () => { } }) {
    const CurrencyInput = useMemo(() => {
        return React.forwardRef((props, ref) => {
            const { onChange, ...other } = props;
            return (
                <NumericFormat
                    {...other}
                    getInputRef={ref}
                    onValueChange={(values) => {
                        onChange({
                            target: {
                                name: props.name,
                                value: values.value,
                            },
                        });
                    }}
                    thousandSeparator
                    valueIsNumericString
                    prefix="$"
                    decimalScale={2}
                    fixedDecimalScale
                />
            );
        });
    }, []);

    const IntegerInput = useMemo(() => {
        return React.forwardRef((props, ref) => {
            const { onChange, ...other } = props;
            return (
                <NumericFormat
                    {...other}
                    getInputRef={ref}
                    onValueChange={(values) => {
                        onChange({
                            target: {
                                name: props.name,
                                value: values.value,
                            },
                        });
                    }}
                    thousandSeparator={false}
                    decimalScale={0}
                    allowNegative={false}
                    valueIsNumericString
                />
            );
        });
    }, []);

    const { openDialog, closeDialog } = useConfirmationDialog();
    const { sendQuoteEmail } = useSendQuoteEmail();
    const openSnackbar = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const scrollableContainerRef = useRef(null);
    const prevRowsLength = useRef(quote?.details?.length || 5);

    const productDetails = quote?.details?.map((detail) => ({
        partNumber: detail.part_number || detail.partNumber,
        itemName: detail.description,
        price: detail.price,
        install: detail.install || 0,
        core: detail.core || 0,
        qty: detail.qty,
        product: detail.productData ? (typeof detail.productData === 'string' ? JSON.parse(detail.productData) : detail.productData) : null,
    }));

    const formik = useFormik({
        initialValues: {
            id: quote.id || null,
            dealerId: dlid || quote.dealerId || Number(localStorage.getItem("_dlid")) || 0,
            quoteRef: quote.quoteRef || "",
            labelManager: quote.assignLabelManager || "",
            description: quote.description || "",
            customerName: quote.billCompany || "",
            customerEmail: quote.billEmail || "",
            rows:
                productDetails ||
                Array.from({ length: 5 }, () => ({
                    partNumber: "",
                    itemName: "",
                    price: "",
                    install: "",
                    core: 0,
                    qty: "",
                    product: null,
                })),
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            const valuesParsed = {
                ...values,
                rows: values.rows.map((row) => {
                    if (row.product && row.product.slugs) {
                        const [
                            make,
                            year,
                            model,
                            trim,
                            engine,
                            category,
                            subcategory,
                        ] = row.product.slugs;
                        const qty = row.qty;
                        const subtotal = row.qty * row.price.price || row.price;

                        return {
                            ...row,
                            product: {
                                ...row.product,
                                quantity: qty,
                                subtotal,
                                catalog_id: row?.product?.catalog?.id,
                                isTaxable: 1,
                                vehicle: {
                                    make,
                                    year,
                                    model,
                                    trim,
                                    engine,
                                    category,
                                    subcategory,
                                },
                            },
                        };
                    }

                    return {
                        ...row,
                        product: {
                            ...row.product,
                        },
                    };
                }),
            };

            if (!formik.values.id) {
                const quote = await postQuote(valuesParsed);
                if (quote && quote.quoteId) {
                    await handleSendEmail({ ...valuesParsed, id: quote.quoteId });
                }
            }
        },
    });

    const handleSendEmail = useCallback(
        async (currentValues) => {
            setIsLoading(true);
            try {
                const payload = {
                    quoteId: currentValues.id || quote.id,
                    dealerId: currentValues.dealerId,
                    quoteRef: currentValues.quoteRef,
                    customerEmail: currentValues.customerEmail,
                    description: currentValues.description,
                    rows: currentValues.rows.filter((row) => row.partNumber),
                };
                await QuoteService.sendEmail(payload);
                openSnackbar("success", "Email sent successfully!");
            } catch (error) {
                console.error("Failed to send email:", error);
                openSnackbar("error", "Failed to send email. Please try again.");
            } finally {
                setIsLoading(false);
            }
        },
        [quote.id, openSnackbar],
    );

    useEffect(() => {
        if (
            scrollableContainerRef.current &&
            formik?.values?.rows?.length > prevRowsLength.current
        ) {
            scrollableContainerRef.current.scrollTop = scrollableContainerRef.current.scrollHeight;
        }
        prevRowsLength.current = formik?.values?.rows?.length;
    }, [formik?.values?.rows?.length]);

    // Sync dealerId from localStorage if not set via props
    useEffect(() => {
        const storedDlid = Number(localStorage.getItem("_dlid"));
        if (storedDlid && !formik.values.dealerId) {
            formik.setFieldValue("dealerId", storedDlid);
        }
    }, [formik.values.dealerId]);

    const postQuote = async (body) => {
        try {
            const quote = await QuoteService.createWithDetails(body);
            onSuccess(quote);
            return quote;
        } catch (error) {
            console.error(error);
        }
    };

    // Función para abrir el diálogo de selección de productos
    const handlePartNumberSearch = useCallback(
        (partNumber, rowIndex) => {
            openDialog({
                customDialog: true,
                fullWidth: true,
                maxWidth: "md",
                message: (
                    <PartSelectionDialog
                        partNumber={partNumber}
                        orderDealerId={formik.values.dealerId}
                        onProductSelect={(product) => {
                            const { name, price: { price } = {}, pathImage } = product;

                            // Case-safe parsing for core charge
                            const parsedCore =
                                product?.core && !isNaN(product.core)
                                    ? parseFloat(product.core)
                                    : 0;

                            // Solo guardamos campos específicos y no `product` directamente
                            formik.setFieldValue(`rows[${rowIndex}].itemName`, name);
                            formik.setFieldValue(`rows[${rowIndex}].price`, price);
                            formik.setFieldValue(`rows[${rowIndex}].core`, parsedCore);
                            formik.setFieldValue(`rows[${rowIndex}].pathImage`, pathImage);
                            formik.setFieldValue(`rows[${rowIndex}].qty`, 1);
                            formik.setFieldValue(`rows[${rowIndex}].product`, product);

                            closeDialog();
                        }}
                    />
                ),
            });
        },
        [formik.values.dealerId, formik.setFieldValue, openDialog, closeDialog],
    );

    const handlePartNumberBlur = useCallback(
        (value, rowIndex, columnId) => {
            formik.setFieldValue(`rows[${rowIndex}].${columnId}`, value);
        },
        [formik.setFieldValue],
    );

    const handleAddRow = useCallback(() => {
        formik.setFieldValue("rows", [
            ...formik.values.rows,
            {
                partNumber: "",
                itemName: "",
                price: "",
                install: "",
                core: 0,
                qty: "",
                product: null,
            },
        ]);
    }, [formik.values.rows, formik.setFieldValue]);

    const partsTotal = useMemo(() => {
        return formik.values.rows.reduce((acc, row) => {
            const price = parseFloat(row.price) || 0;
            const qty = parseInt(row.qty) || 0;
            return acc + price * qty;
        }, 0);
    }, [formik.values.rows]);

    const installTotal = useMemo(() => {
        return formik.values.rows.reduce((acc, row) => {
            const install = parseFloat(row.install) || 0;
            const qty = parseInt(row.qty) || 0;
            return acc + install * qty;
        }, 0);
    }, [formik.values.rows]);

    const grandTotal = partsTotal + installTotal;

    console.log("formik.values", formik.values);

    const columns = useMemo(
        () => [
            {
                Header: "Part Number",
                accessor: "partNumber",
                align: "left",
                Cell: ({ value, row, column }) => (
                    <TextFieldTableCell
                        fullWidth
                        name={`rows[${row.index}].${column.id}`}
                        value={value}
                        onBlur={(val) => handlePartNumberBlur(val, row.index, column.id)}
                        onChange={(val) =>
                            formik.setFieldValue(`rows[${row.index}].${column.id}`, val)
                        }
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        disabled={!value || !value.toString().trim()}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={(e) => {
                                            const inputElement = e.currentTarget
                                                .closest(".MuiInputBase-root")
                                                ?.querySelector("input");
                                            const val = inputElement
                                                ? inputElement.value
                                                : row.original[column.id];

                                            if (!val || !val.toString().trim()) return;

                                            handlePartNumberBlur(val, row.index, column.id);
                                            handlePartNumberSearch(val, row.index);
                                        }}
                                        edge="end"
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                ),
            },
            {
                Header: "Item Name",
                accessor: "itemName",
                align: "left",
                Cell: ({ value, row, column }) => (
                    <TextFieldTableCell
                        fullWidth
                        name={`rows[${row.index}].${column.id}`}
                        value={value}
                        onBlur={(val) => formik.setFieldValue(`rows[${row.index}].${column.id}`, val)}
                        onChange={(val) =>
                            formik.setFieldValue(`rows[${row.index}].${column.id}`, val)
                        }
                    />
                ),
            },
            {
                Header: "Price",
                accessor: "price",
                align: "right",
                Cell: ({ value, row, column }) => (
                    <TextFieldTableCell
                        fullWidth
                        name={`rows[${row.index}].${column.id}`}
                        value={value}
                        inputProps={{
                            style: { textAlign: "right" },
                        }}
                        onBlur={(val) => formik.setFieldValue(`rows[${row.index}].${column.id}`, val)}
                        onChange={(val) =>
                            formik.setFieldValue(`rows[${row.index}].${column.id}`, val)
                        }
                        InputProps={{
                            inputComponent: CurrencyInput,
                        }}
                    />
                ),
            },
            {
                Header: "Install",
                accessor: "install",
                align: "right",
                Cell: ({ value, row, column }) => (
                    <TextFieldTableCell
                        fullWidth
                        name={`rows[${row.index}].${column.id}`}
                        value={value}
                        inputProps={{
                            style: { textAlign: "right" },
                        }}
                        onBlur={(val) => formik.setFieldValue(`rows[${row.index}].${column.id}`, val)}
                        onChange={(val) =>
                            formik.setFieldValue(`rows[${row.index}].${column.id}`, val)
                        }
                        InputProps={{
                            inputComponent: CurrencyInput,
                        }}
                    />
                ),
            },
            {
                Header: "Qty",
                accessor: "qty",
                align: "center",
                width: 100,
                Cell: ({ value, row, column }) => (
                    <TextFieldTableCell
                        fullWidth
                        name={`rows[${row.index}].${column.id}`}
                        value={value}
                        inputProps={{
                            style: { textAlign: "center" },
                        }}
                        onBlur={(val) => formik.setFieldValue(`rows[${row.index}].${column.id}`, val)}
                        onChange={(val) =>
                            formik.setFieldValue(`rows[${row.index}].${column.id}`, val)
                        }
                        InputProps={{
                            inputComponent: IntegerInput,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <IconButton
                                        size="small"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            const currentVal = parseInt(value) || 0;
                                            if (currentVal > 1) {
                                                formik.setFieldValue(
                                                    `rows[${row.index}].${column.id}`,
                                                    currentVal - 1,
                                                );
                                            }
                                        }}
                                    >
                                        <RemoveIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        style={{ padding: 0 }}
                                        onClick={() => {
                                            const currentVal = parseInt(value) || 0;
                                            formik.setFieldValue(
                                                `rows[${row.index}].${column.id}`,
                                                currentVal + 1,
                                            );
                                        }}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                ),
            },
            {
                Header: "Parts Total",
                accessor: "subtotal",
                align: "right",
                Cell: ({ row }) => {
                    const { price, qty, core } = row.original;
                    const subtotal = (Number(price) || 0) * (Number(qty) || 0);

                    const coreValue = parseFloat(core);
                    const hasCore = core && !isNaN(coreValue) && coreValue > 0;

                    return (
                        <Box display="flex" alignItems="center" height="100%">
                            <TextFieldTableCell
                                fullWidth
                                type="text"
                                name={`rows[${row.index}].subtotal`}
                                value={subtotal.toFixed(2)}
                                onBlur={() => { }}
                                inputProps={{
                                    style: { textAlign: "right" },
                                }}
                                InputProps={{
                                    readOnly: true,
                                    inputComponent: CurrencyInput,
                                    endAdornment: hasCore ? (
                                        <InputAdornment position="end">
                                            <Tooltip
                                                title={
                                                    <Box p={1}>
                                                        <Typography
                                                            variant="body2"
                                                            style={{ fontSize: "12px" }}
                                                        >
                                                            This item has a core charge of $
                                                            {coreValue.toFixed(2)}. Already included
                                                            in the price.
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            style={{
                                                                fontSize: "12px",
                                                                marginTop: "8px",
                                                            }}
                                                        >
                                                            If you return your old/defective item
                                                            back, you may be eligible for the core
                                                            amount refunded.
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                            >
                                                <IconButton size="small" style={{ padding: 0 }}>
                                                    <InfoOutlinedIcon
                                                        style={{
                                                            fontSize: "18px",
                                                            color: "#999",
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                        </Box>
                    );
                },
            },
        ],
        [handlePartNumberBlur, handlePartNumberSearch, formik.setFieldValue],
    );

    return (
        <Box>
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Box mt={1} mb={3} display="flex" justifyContent="flex-start">
                        <SelectDealerGrouped />
                    </Box>
                    <Divider style={{ marginBottom: "24px" }} />
                </GridItem>
            </GridContainer>

            <form onSubmit={formik.handleSubmit} style={{ position: "relative" }}>
                {!Number(formik.values.dealerId) && (
                    <Box
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 100,
                            backgroundColor: "rgba(255, 255, 255, 0.7)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderRadius: "8px",
                            cursor: "not-allowed",
                        }}
                    >
                        <Box
                            p={3}
                            bgcolor="white"
                            boxShadow={3}
                            borderRadius={8}
                            textAlign="center"
                            style={{ maxWidth: "80%" }}
                        >
                            <Typography variant="h6" color="textSecondary" gutterBottom>
                                Action Required
                            </Typography>
                            <Typography variant="body1" color="textSecondary">
                                Please select a dealer from the dropdown above to start creating or
                                editing this quote.
                            </Typography>
                        </Box>
                    </Box>
                )}
                <GridContainer>
                    <GridItem xs={6} sm={6} md={6}>
                        <TextFieldRender
                            fullWidth
                            margin="dense"
                            id="quoteRef"
                            name="quoteRef"
                            label="Quote Ref#"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.quoteRef}
                            error={formik.touched.quoteRef && Boolean(formik.errors.quoteRef)}
                            helperText={formik.touched.quoteRef && formik.errors.quoteRef}
                        />
                    </GridItem>
                    <GridItem xs={6} sm={6} md={6}>
                        <TextFieldRender
                            fullWidth
                            margin="dense"
                            id="labelManager"
                            name="labelManager"
                            label="Assign Label Manager"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.labelManager}
                            error={
                                formik.touched.labelManager && Boolean(formik.errors.labelManager)
                            }
                            helperText={formik.touched.labelManager && formik.errors.labelManager}
                        />
                    </GridItem>
                    <GridItem xs={12} sm={12} md={12}>
                        <TextFieldRender
                            fullWidth
                            multiline
                            margin="dense"
                            id="description"
                            name="description"
                            label="Description"
                            rows={2}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.description}
                        />
                    </GridItem>
                    <GridItem xs={6} sm={6} md={6}>
                        <TextFieldRender
                            fullWidth
                            margin="dense"
                            id="customerName"
                            name="customerName"
                            label="Customer Name"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.customerName}
                            error={
                                formik.touched.customerName && Boolean(formik.errors.customerName)
                            }
                            helperText={formik.touched.customerName && formik.errors.customerName}
                        />
                    </GridItem>
                    <GridItem xs={6} sm={6} md={6}>
                        <TextFieldRender
                            fullWidth
                            margin="dense"
                            id="customerEmail"
                            name="customerEmail"
                            label="Customer Email"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.customerEmail}
                            error={
                                formik.touched.customerEmail && Boolean(formik.errors.customerEmail)
                            }
                            helperText={formik.touched.customerEmail && formik.errors.customerEmail}
                        />
                    </GridItem>
                    <GridItem xs={12} sm={12} md={12}>
                        <Box
                            ref={scrollableContainerRef}
                            style={{
                                maxHeight: "300px",
                                overflowY: "auto",
                                border: "1px solid #eeeeee",
                                borderBottom: "none",
                                borderRadius: "4px 4px 0 0",
                            }}
                        >
                            <Table
                                columns={columns}
                                data={formik.values.rows}
                                fetchData={() => { }}
                                pageCount={1}
                                pageSize={formik.values.rows.length}
                            />
                        </Box>
                        <Box
                            display="flex"
                            justifyContent="center"
                            style={{
                                border: "1px solid #eeeeee",
                                borderTop: "none",
                                borderRadius: "0 0 4px 4px",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <Button
                                fullWidth
                                onClick={handleAddRow}
                                startIcon={<AddIcon />}
                                style={{
                                    textTransform: "none",
                                    padding: "4px 0",
                                    color: "#757575",
                                }}
                            >
                                Add Item
                            </Button>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={1}>
                            <Box
                                p={1.5}
                                style={{
                                    backgroundColor: "#fcfcfc",
                                    borderRadius: "4px",
                                    border: "1px solid #eeeeee",
                                    minWidth: "220px",
                                }}
                            >
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="textSecondary">
                                        Parts Total:
                                    </Typography>
                                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                                        ${partsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                    <Typography variant="caption" color="textSecondary">
                                        Installation Total:
                                    </Typography>
                                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                                        ${installTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                                <Divider style={{ margin: "4px 0" }} />
                                <Box display="flex" justifyContent="space-between">
                                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                                        Grand Total:
                                    </Typography>
                                    <Typography variant="body2" color="primary" style={{ fontWeight: 700 }}>
                                        ${grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {formik.errors.rows && (
                            <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                                {formik.errors.rows}
                            </Typography>
                        )}
                    </GridItem>
                    {!formik.values.id ? (
                        <GridItem
                            xs={12}
                            sm={12}
                            md={12}
                            style={{ textAlign: "center", marginTop: 10 }}
                        >
                            <Button
                                type="submit"
                                variant="contained"
                                color="secondary"
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isLoading ? "Processing..." : "Save and Send Email"}
                            </Button>
                        </GridItem>
                    ) : (
                        <GridItem
                            xs={12}
                            sm={12}
                            md={12}
                            style={{ textAlign: "center", marginTop: 10 }}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSendEmail(formik.values)}
                                disabled={isLoading}
                                startIcon={isLoading ? <CircularProgress size={20} /> : null}
                            >
                                {isLoading ? "Sending..." : "Resend Email"}
                            </Button>
                        </GridItem>
                    )}
                </GridContainer>
            </form>
        </Box>
    );
}
